import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';
import { ProgressTypeDb } from 'lib/cosmos/progress-type/progressTypeDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { SelectItemField } from 'lib/ui/form/fields/select-item-field.component.js';
import { IMultiSeries, ISingleSeries } from 'lib/ui/graphs/index.js';
import { InfoTableItem } from 'lib/ui/simple-table/info-table.component.js';
import { ITable } from 'lib/ui/table/index.js';
import { CoreAppComponent } from 'src/app/core/core-app.component.js';
import { progressHelper } from './progress-helper.class.js';

type ExtendedProgressType = UserDb.IUserAssignedProgressType & {
	readonly name: string;
	readonly multiSeries: readonly IMultiSeries[];
	readonly yLabel?: string;
	readonly xLabel?: string;
	readonly progressType: ProgressTypeDb.IProgressTypeItem | undefined;
};

type MeasurementItem = UserDb.IUserAssignedProgressTypeValue & {
	readonly id: string;
};

@Component({
	templateUrl: './progress-graphs.component.html',
	selector: 'mod-progress-graphs',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ProgressGraphsComponent extends CoreAppComponent {
	public readonly assignedUserProgressTypes = input.required<readonly UserDb.IUserAssignedProgressType[]>();
	public readonly allProgressTypes = input.required<readonly ProgressTypeDb.IProgressTypeItem[]>();
	public readonly displayOverviewTable = input<boolean>(false);

	public readonly selectedTypeId = signal<string | undefined>(undefined);

	public readonly availableTypesSelectionList = computed<SelectItemField<ExtendedProgressType>[]>(() => {
		return this.assignedUserProgressTypes().map<SelectItemField<ExtendedProgressType>>((assignedProgressType) => {
			const progressType = this.allProgressTypes().find((m) => m.id === assignedProgressType.progressTypeId);
			const name = super.getRequiredItemName(progressType?.system.name);

			const usedUnit = progressHelper.convertValueToPrefferedFormat({
				preferredUnit: this.authUser()?.preferredUnit,
				unit: progressType?.unit,
				value: 0
			}).unit;

			const extendedType: ExtendedProgressType = {
				...assignedProgressType,
				name: `${name} `,
				multiSeries: this.getMultiSeries(name, assignedProgressType.values, progressType?.unit),
				yLabel: usedUnit,
				progressType: progressType
			};

			const item: SelectItemField<ExtendedProgressType> = {
				data: extendedType,
				title: extendedType.name,
				value: assignedProgressType.id,
				subtitle: usedUnit
			};

			return item;
		});
	});

	public readonly selectedType = computed<ExtendedProgressType | undefined>(() => {
		const selectedTypeId = this.selectedTypeId();

		if (!selectedTypeId) {
			return undefined;
		}

		const selectionListItem = this.availableTypesSelectionList().find((m) => m.data.id === selectedTypeId);

		if (selectionListItem) {
			return selectionListItem.data;
		}

		return undefined;
	});

	public readonly table = computed<ITable<MeasurementItem> | undefined>(() => {
		const selectedType = this.selectedType();

		if (!selectedType) {
			return undefined;
		}

		return this.getTable(selectedType);
	});

	public readonly overviewTableItems = computed<readonly InfoTableItem[] | undefined>(() => {
		if (!this.displayOverviewTable()) {
			return undefined;
		}

		const selectedType = this.selectedType();

		if (!selectedType) {
			return undefined;
		}

		const overviewData = progressHelper.calculateMeasurementsOverview(
			selectedType.values,
			this.authUser(),
			selectedType.progressType?.unit
		);

		return [
			{
				title: 'Measurements',
				value: overviewData.measurements.toString()
			},
			{
				title: 'Min. value',
				value: `${overviewData.minValue} ${selectedType.yLabel}`
			},
			{
				title: 'Max. value',
				value: `${overviewData.maxValue} ${selectedType.yLabel}`
			},
			{
				title: 'Average value',
				value: `${overviewData.avgValue} ${selectedType.yLabel}`
			}
		];
	});

	public readonly measurementsInfoTableItems = computed<readonly InfoTableItem[] | undefined>(() => {
		const selectedType = this.selectedType();

		if (!selectedType) {
			return undefined;
		}

		return this.getMeasurementsInfoTableItems(selectedType);
	});

	constructor() {
		super();
		effect(() => {
			const assignedProgressTypes = this.assignedUserProgressTypes();

			if (!this.selectedTypeId()) {
				this.selectedTypeId.set(assignedProgressTypes?.[0]?.id);
			}
		});
	}

	private getMeasurementsInfoTableItems(type: ExtendedProgressType): readonly InfoTableItem[] {
		return type.values.map<InfoTableItem>((m) => {
			return {
				title: this.dependencies.timeService.formatDayMonthAndYear(m.date),
				value: `${m.value} ${type.yLabel}`
			};
		});
	}

	private getTable(type: ExtendedProgressType): ITable<MeasurementItem> {
		return {
			search: {
				type: 'local'
			},
			noItemsText: {
				noItems: `You have no measurements for this type`,
				noFilteredItems: `No measurements matches your filter`
			},
			items: type.values.map<MeasurementItem>((m) => ({
				...m,
				id: m.date.toString()
			})),
			id: (item) => item.id,
			title: {
				title: `${type.name} (${type.yLabel})`,
				value: (item) => item.value.toString()
			},
			fields: [
				{
					format: 'date',
					title: `Date`,
					value: (item) => this.dependencies.timeService.formatDayMonthAndYear(item.date),
					sortBy: (item) => new Date(item.date),
					isDefaultSort: true
				}
			]
		};
	}

	private getMultiSeries(
		name: string,
		values: readonly UserDb.IUserAssignedProgressTypeValue[],
		unit: ProgressTypeDb.ProgressTypeUnit | undefined
	): readonly IMultiSeries[] {
		const orderedValues = values.toSorted((a, b) => {
			const aDate = this.dependencies.timeService.getLuxonDateTime(a.date);
			const bDate = this.dependencies.timeService.getLuxonDateTime(b.date);

			return aDate.toJSDate().valueOf() - bDate.toJSDate().valueOf();
		});

		const series: IMultiSeries[] = [
			{
				name: name,
				series: orderedValues.map<ISingleSeries>((m) => {
					return {
						name: this.dependencies.timeService.formatDayMonthAndYear(m.date),
						value: progressHelper.convertValueToPrefferedFormat({
							preferredUnit: this.authUser()?.preferredUnit,
							unit: unit,
							value: m.value
						}).value
					};
				})
			}
		];

		return series;
	}
}
